import React, { useState, useEffect } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  sub: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  overviewGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px', marginBottom: '24px',
  },
  statCard: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '20px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  statLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statValue: { fontSize: '32px', fontWeight: '800', color: '#0052CC', margin: '8px 0 4px' },
  statSub: { fontSize: '12px', color: '#9ca3af' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' },
  card: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardFull: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px', fontWeight: '700', color: '#0052CC',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '20px',
    paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
  },
  barRow: { marginBottom: '14px' },
  barHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  barLabel: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  barValue: { fontSize: '13px', fontWeight: '700', color: '#0052CC' },
  barTrack: {
    width: '100%', height: '8px', backgroundColor: '#e5e7eb',
    borderRadius: '4px', overflow: 'hidden',
  },
  barFill: { height: '100%', borderRadius: '4px', transition: 'width 0.5s ease' },
  barSub: { fontSize: '11px', color: '#9ca3af', marginTop: '3px' },
  providerCard: {
    border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px',
    marginBottom: '12px',
  },
  providerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  providerName: { fontSize: '15px', fontWeight: '700', color: '#111827' },
  providerScore: {
    fontSize: '20px', fontWeight: '800', color: '#0052CC',
  },
  providerMeta: { fontSize: '12px', color: '#6b7280', marginBottom: '8px' },
  modelRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '6px 0', borderTop: '1px solid #f3f4f6', fontSize: '13px',
  },
  modelName: { color: '#374151', fontWeight: '500' },
  modelScore: { fontWeight: '700', color: '#0052CC' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 12px',
    borderBottom: '2px solid #e5e7eb',
  },
  td: {
    padding: '10px 12px', fontSize: '13px', color: '#374151',
    borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle',
  },
  statusBadge: {
    display: 'inline-block', padding: '2px 8px', borderRadius: '20px',
    fontSize: '11px', fontWeight: '700',
  },
  scoreBadge: {
    display: 'inline-block', padding: '2px 10px', borderRadius: '20px',
    fontSize: '12px', fontWeight: '700', backgroundColor: '#EBF5FF', color: '#0052CC',
  },
  viewBtn: {
    padding: '4px 12px', backgroundColor: '#f3f4f6', color: '#374151',
    border: 'none', borderRadius: '6px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer',
  },
  empty: { textAlign: 'center', padding: '40px', color: '#9ca3af', fontSize: '13px' },
  loading: { textAlign: 'center', padding: '64px', color: '#6b7280' },
  trendDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    display: 'inline-block', marginRight: '6px',
  },
};

const STATUS_COLORS = {
  completed: { backgroundColor: '#dcfce7', color: '#16a34a' },
  stopped: { backgroundColor: '#fef3c7', color: '#d97706' },
  failed: { backgroundColor: '#fef2f2', color: '#dc2626' },
  running: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
};

function getScoreColor(score) {
  if (score === null || score === undefined) return '#9ca3af';
  if (score >= 80) return '#16a34a';
  if (score >= 60) return '#0052CC';
  if (score >= 40) return '#d97706';
  return '#dc2626';
}
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function groupByMonthYear(items) {
  const groups = {};
  items.forEach(item => {
    const date = new Date(item.created_at);
    const year = date.getFullYear();
    const month = date.getMonth();
    const key = `${year}-${month}`;
    if (!groups[key]) groups[key] = { year, month, items: [] };
    groups[key].items.push(item);
  });
  return Object.values(groups).sort((a, b) =>
    b.year !== a.year ? b.year - a.year : b.month - a.month
  );
}

export default function AnalyticsDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [providers, setProviders] = useState([]);
  const [history, setHistory] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [openGroups, setOpenGroups] = useState({});


  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const uid = currentUser.uid;
      const [ovRes, metricsRes, providersRes, historyRes, scoresRes] = await Promise.all([
        fetch(`${API}/api/analytics/overview?owner_id=${uid}`),
        fetch(`${API}/api/analytics/metrics?owner_id=${uid}`),
        fetch(`${API}/api/analytics/providers?owner_id=${uid}`),
        fetch(`${API}/api/analytics/history?owner_id=${uid}`),
        fetch(`${API}/api/analytics/scores?owner_id=${uid}`),
      ]);
      const [ovData, metricsData, providersData, historyData, scoresData] = await Promise.all([
        ovRes.json(), metricsRes.json(), providersRes.json(),
        historyRes.json(), scoresRes.json(),
      ]);
      setOverview(ovData);
      setMetrics(Array.isArray(metricsData) ? metricsData : []);
      setProviders(Array.isArray(providersData) ? providersData : []);
      setHistory(Array.isArray(historyData) ? historyData : []);
      setScores(Array.isArray(scoresData) ? scoresData : []);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  // Simple score trend — last 10 evaluations
  const recentScores = scores.slice(-10);

  if (loading) return <AppLayout><div style={styles.loading}>Loading analytics...</div></AppLayout>;

  return (
    <AppLayout>
      <div style={styles.header}>
        <div style={styles.title}>Analytics</div>
        <div style={styles.sub}>Performance insights across all simulations</div>
      </div>

      {/* Overview Stats */}
      <div style={styles.overviewGrid}>
        {[
          { label: 'Total Simulations', value: overview?.total_simulations ?? 0, sub: 'all runs' },
          { label: 'Completed', value: overview?.completed_simulations ?? 0, sub: 'finished runs' },
          { label: 'Avg WER', value: overview?.avg_wer != null ? `${Math.round(overview.avg_wer * 100)}%` : '—', sub: 'word error rate' },
          { label: 'Avg Score', value: overview?.avg_overall_score ?? '—', sub: 'overall score /100' },
          { label: 'Scenarios', value: overview?.total_scenarios ?? 0, sub: 'configured' },
          { label: 'Agents', value: overview?.total_agents ?? 0, sub: 'created' },
          { label: 'Evaluations', value: overview?.total_evaluations ?? 0, sub: 'completed' },
        ].map(stat => (
          <div key={stat.label} style={styles.statCard}>
            <div style={styles.statLabel}>{stat.label}</div>
            <div style={styles.statValue}>{stat.value}</div>
            <div style={styles.statSub}>{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* Score Trend + Metric Breakdown */}
      <div style={{ ...styles.grid2, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>

        {/* Score Trend */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Recent Score Trend</div>
          {recentScores.length === 0 ? (
            <div style={styles.empty}>No evaluations yet.</div>
          ) : (
            recentScores.map((s, idx) => (
              <div key={s.id} style={styles.barRow}>
                <div style={styles.barHeader}>
                  <div style={styles.barLabel}>
                    Run {idx + 1}
                    <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '8px' }}>
                      {s.llm_provider} · {s.llm_model}
                    </span>
                  </div>
                  <div style={{ ...styles.barValue, color: getScoreColor(s.overall_score) }}>
                    {s.overall_score}/100
                  </div>
                </div>
                <div style={styles.barTrack}>
                  <div style={{
                    ...styles.barFill,
                    width: `${s.overall_score}%`,
                    backgroundColor: getScoreColor(s.overall_score),
                  }} />
                </div>
                <div style={styles.barSub}>
                  {new Date(s.created_at).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Metric Breakdown */}
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Avg Score by Metric</div>
          {metrics.length === 0 ? (
            <div style={styles.empty}>No evaluation data yet.</div>
          ) : (
            metrics.map(m => (
              <div key={m.metric} style={styles.barRow}>
                <div style={styles.barHeader}>
                  <div style={styles.barLabel}>{m.metric}</div>
                  <div style={{ ...styles.barValue, color: getScoreColor(m.avg_score) }}>
                    {m.avg_score != null ? `${m.avg_score}/100` : '—'}
                  </div>
                </div>
                <div style={styles.barTrack}>
                  <div style={{
                    ...styles.barFill,
                    width: `${m.avg_score ?? 0}%`,
                    backgroundColor: getScoreColor(m.avg_score),
                  }} />
                </div>
                <div style={styles.barSub}>{m.run_count} run{m.run_count !== 1 ? 's' : ''}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Provider Benchmarks */}
      <div style={styles.cardFull}>
        <div style={styles.sectionTitle}>Provider Benchmarks</div>
        {providers.length === 0 ? (
          <div style={styles.empty}>No provider data yet.</div>
        ) : (
          providers.map(p => (
            <div key={p.provider} style={styles.providerCard}>
              <div style={styles.providerHeader}>
                <div style={styles.providerName}>
                  {p.provider.charAt(0).toUpperCase() + p.provider.slice(1)}
                </div>
                <div style={{ ...styles.providerScore, color: getScoreColor(p.avg_score) }}>
                  {p.avg_score != null ? `${p.avg_score}/100` : '—'}
                </div>
              </div>
              <div style={styles.providerMeta}>
                {p.run_count} run{p.run_count !== 1 ? 's' : ''} · avg overall score
              </div>
              {p.models.map(m => (
                <div key={m.model} style={styles.modelRow}>
                  <div style={styles.modelName}>{m.model}</div>
                  <div style={{ ...styles.modelScore, color: getScoreColor(m.avg_score) }}>
                    {m.avg_score != null ? `${m.avg_score}/100` : '—'} · {m.run_count} run{m.run_count !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Simulation History — grouped by month/year */}
      <div style={styles.cardFull}>
        <div style={styles.sectionTitle}>Simulation History</div>
        {history.length === 0 ? (
          <div style={styles.empty}>No simulations yet.</div>
        ) : (
          groupByMonthYear(history).map(group => {
            const key = `${group.year}-${group.month}`;
            const isOpen = openGroups[key] === true; // default closed
            return (
              <div key={key} style={{ marginBottom: '12px' }}>
                <div
                  onClick={() => setOpenGroups(prev => ({ ...prev, [key]: !isOpen }))}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', backgroundColor: '#f9fafb', borderRadius: '8px',
                    cursor: 'pointer', marginBottom: isOpen ? '10px' : '0',
                  }}
                >
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                    {MONTH_NAMES[group.month]} {group.year} ({group.items.length})
                  </span>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>{isOpen ? '▲' : '▼'}</span>
                </div>

                {isOpen && (
                  <div style={{ overflowX: isMobile ? 'auto' : 'visible' }}>
                    <table style={{ ...styles.table, minWidth: isMobile ? '600px' : 'auto' }}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Scenario</th>
                          <th style={styles.th}>Provider</th>
                          <th style={styles.th}>Model</th>
                          <th style={styles.th}>Turns</th>
                          <th style={styles.th}>Score</th>
                          <th style={styles.th}>WER</th>
                          <th style={styles.th}>Status</th>
                          <th style={styles.th}>Date</th>
                          <th style={styles.th}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map(sim => (
                          <tr key={sim.id}>
                            <td style={styles.td}>{sim.scenario_title}</td>
                            <td style={styles.td}>{sim.llm_provider}</td>
                            <td style={styles.td}>{sim.llm_model}</td>
                            <td style={styles.td}>{sim.current_turn}/{sim.max_turns}</td>
                            <td style={styles.td}>
                              {sim.overall_score !== null ? (
                                <span style={{
                                  ...styles.scoreBadge,
                                  backgroundColor: `${getScoreColor(sim.overall_score)}20`,
                                  color: getScoreColor(sim.overall_score),
                                }}>
                                  {sim.overall_score}/100
                                </span>
                              ) : '—'}
                            </td>
                            <td style={styles.td}>
                              {sim.mean_wer !== null && sim.mean_wer !== undefined ? (
                                <span style={{
                                  fontSize: '12px', fontWeight: '700', padding: '2px 8px',
                                  borderRadius: '20px',
                                  backgroundColor: sim.mean_wer > 0.5 ? '#fef2f2' : sim.mean_wer > 0.2 ? '#fef3c7' : '#f0fdf4',
                                  color: sim.mean_wer > 0.5 ? '#dc2626' : sim.mean_wer > 0.2 ? '#d97706' : '#16a34a',
                                }}>
                                  {Math.round(sim.mean_wer * 100)}%
                                </span>
                              ) : <span style={{ fontSize: '12px', color: '#9ca3af' }}>—</span>}
                            </td>
                            <td style={styles.td}>
                              <span style={{
                                ...styles.statusBadge,
                                ...(STATUS_COLORS[sim.status] || STATUS_COLORS.completed),
                              }}>
                                {sim.status}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {new Date(sim.created_at).toLocaleDateString()}
                            </td>
                            <td style={styles.td}>
                              <button
                                style={styles.viewBtn}
                                onClick={() => navigate(`/results/${sim.id}`)}
                              >
                                View →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </AppLayout>
  );
}
